import OptionsSummary from '../../../../../shared/js/components/OptionsSummary'
import { formatPrice } from '../../../../../shared/js/formatters'
import { drop, get } from '../../../../../shared/js/json-fetch'
import { computeOptionsCents } from '../../../../../shared/js/options-utils'
import { notifyCartBadge } from '../../Cart'
import Dialog from '../../shared/Dialog'
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import type { FlashMessage } from '../../../../../back/v2/js/shared/Module'
import type { LineItemProps } from '../../../shared/Module'
import type { MouseEventHandler } from 'react'

interface CartDialogProps {
  onClose: MouseEventHandler<HTMLButtonElement>
  opened: boolean
  title: string
  relatedProductsHTML?: string
  cartViewURL: string
}
interface CartDialogContentProps {
  cartViewURL: string
  onClose: MouseEventHandler<HTMLButtonElement>
  relatedProductsHTML?: string
}

type CartDataProps = {
  cartRemoveURL: string
  delivery: {
    cents: number
    label: string
  }
  lineItems: LineItemProps[]
}

const queryClient = new QueryClient()
const CART_QUERY_KEY = 'cart-view'

export default function CartDialog({
  cartViewURL,
  onClose,
  opened,
  title,
  ...props
}: CartDialogProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <Dialog
        className='cart-panel panel-content right-modal'
        onCancel={onClose}
        opened={opened}
        title={title}
      >
        <button
          type='button'
          className='right-action-modal-close'
          onClick={onClose}
        >
          <span>Fermer</span>
        </button>
        <div className='inner-row' style={{ padding: 0 }}>
          {opened && (
            <CartDialogContent
              {...props}
              onClose={onClose}
              cartViewURL={cartViewURL}
            />
          )}
        </div>
      </Dialog>
    </QueryClientProvider>
  )
}

function CartDialogContent({
  onClose,
  relatedProductsHTML,
  cartViewURL,
}: CartDialogContentProps) {
  const [flash, setFlash] = useState<FlashMessage | null>(null)

  // Queries
  const { isPending, error, data } = useQuery({
    queryKey: [CART_QUERY_KEY],
    queryFn: async () => await get(cartViewURL),
  })

  const errorMsg = error?.message || flash?.message

  if (errorMsg) {
    return <div className={`c-message c-message--error`}>{errorMsg}</div>
  }

  if (isPending) {
    return <p>Chargement du panier…</p>
  }

  const { cartRemoveURL, delivery, lineItems }: CartDataProps = data || {}
  const totalCents = computeTotalCents(lineItems)

  return (
    <>
      <div className='products-list'>
        {!lineItems || lineItems.length === 0
          ? 'Vous n’avez pas de produit dans votre panier'
          : lineItems.map((li) => (
              <CartLineItem {...li} key={li.id} cartRemoveURL={cartRemoveURL} />
            ))}
      </div>

      {relatedProductsHTML && (
        <div dangerouslySetInnerHTML={{ __html: relatedProductsHTML }} />
      )}

      {totalCents && (
        <dl className='order-summary'>
          {delivery && (
            <div>
              <dt>Livraison</dt>
              <dd className='main'>
                {delivery.cents === 0
                  ? 'gratuite'
                  : formatPrice(delivery.cents)}
              </dd>
              <dd className='detail'>{delivery.label}</dd>
            </div>
          )}
          <div>
            <dt>Total</dt>
            <dd className='main'>
              <span className='price'>{formatPrice(totalCents)}</span>
            </dd>
            {/* TODO: paiement en plusieurs fois : Alma ? */}
            {/* <dd className="detail total">
                  <span className="price">
                    <span>ou 3 x 312,66€
                      <button className="tooltip-trigger" aria-describedby="tooltip-paymentplan-tooltip">
                        <span className="visually-hidden">En savoir plus</span>
                        <span className="tooltip" role="tooltip" id="tooltip-paymentplan-tooltip" style={{ transform: 'translateX(calc(-50% - 1643.33px));' }}>
                          ● Paiement en 3 fois sans frais proposé à partir de 350€ d’achat<br><br>● Vous étalez votre paiement sur 3 mois consécutifs
                        </span>
                      </button>
                    </span>
                  </span>
                </dd> */}
          </div>
        </dl>
      )}

      {/* TODO: gérer l'option papier cadeau */}
      {/* <div className='c-added-to-cart__gift'>
                  <input id='gift' type='checkbox' name='gift' />
                  <label
                    className='c-label c-label--checkbox c-label--checkbox c-label--highlight'
                    htmlFor='gift'
                  >
                    option papier cadeau (+ 2€)
                  </label>
                </div> */}

      <p className='buttons'>
        <button className='lb-btn lb-btn-type-7' onClick={onClose}>
          {totalCents ? 'Continuer mes achats' : 'Fermer'}
        </button>
        {totalCents && (
          <a href={cartViewURL} className='lb-btn lb-btn-type-3'>
            {/* TODO: afficher le montant  */}
            {formatPrice(totalCents)} • Commander
          </a>
        )}
      </p>
    </>
  )

  function computeTotalCents(lineItems: LineItemProps[]) {
    if (!lineItems?.length) return null
    // Computes the total cents
    let totalCents = 0
    for (const { options, quantity, unitCents, unitTaxCents } of lineItems) {
      const { optionsCents, optionsTaxCents } = computeOptionsCents(options)
      const extraCents = quantity * (unitCents + optionsCents)
      const extraTaxCents = quantity * (unitTaxCents + optionsTaxCents)

      totalCents += extraCents + extraTaxCents
    }
    return totalCents
  }
}

interface CartLineItemProps extends LineItemProps {
  cartRemoveURL: string
}

function CartLineItem({
  htmlPicture,
  id,
  options,
  name,
  quantifiedCents,
  quantity,
  url,
  cartRemoveURL,
}: CartLineItemProps) {
  const removeLineItemMutation = useMutation({
    mutationFn: removeLineItem,
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] })
    },
  })

  if (removeLineItemMutation.isError) {
    return (
      <p>
        Une erreur est survenue, la suppression n’a pas pu être effectuée. (
        {removeLineItemMutation.error.message})
      </p>
    )
  }

  return (
    <article
      className='card cart-product new'
      key={`li-${id}`}
      style={{ opacity: removeLineItemMutation.isPending ? 0.5 : 1 }}
    >
      {htmlPicture && (
        <figure
          className='card-media'
          dangerouslySetInnerHTML={{ __html: htmlPicture }}
        />
      )}
      <header>
        <h2 className='h2'>
          <a href={url}>{name}</a>
        </h2>
        {options && <div>{<OptionsSummary options={options} />}</div>}
      </header>
      <p className='actions'>
        <strong className='price'>{formatPrice(quantifiedCents)}</strong>
        <button
          className='lb-btn lb-btn-naked remove'
          onClick={() => removeLineItemMutation.mutate()}
        >
          <span>Retirer du panier</span>
        </button>
      </p>
    </article>
  )

  async function removeLineItem() {
    const { data, flash } = await drop(cartRemoveURL, { line_item: { id } })
    if (flash) throw flash.message
    data && notifyCartBadge(data.size)
  }
}
